import React from 'react';
import { motion } from 'framer-motion';
import { Award, GraduationCap, Globe2, Users, Tv2, Star, Sparkles } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { trackWhatsAppClick, trackConsultationCTAClick } from '../utils/analytics';

/**
 * TrustBlock — sits directly below the Hero on the homepage.
 *
 * Purpose: convince every new visitor (within 8 seconds) that Acharya Vivek जी
 * is a legitimate, media-recognised, 3-decade-experienced authority.
 * Without this block, the GA4 bounce rate is dominated by skeptical first-time
 * visitors who have no proof signal above the fold.
 *
 * Visible signals (P2 from the audit):
 *   • 33+ Years of Vedic practice
 *   • Acharya in Phalit Jyotish
 *   • Gurukul-rooted education
 *   • Media: Zee News, Aaj Tak, ABP News, News18
 *   • Trusted by Dubai clients since 2001
 *   • 33,300+ YouTube subscribers
 *
 * Plus a tight pair of CTAs (Book + WhatsApp) so the trust momentum
 * translates immediately to a conversion.
 */

const STATS = [
  { icon: Award,         hi: '33+ वर्ष',           en: '33+ Years',          sub_hi: 'Vedic Practice',         sub_en: 'Vedic Practice' },
  { icon: GraduationCap, hi: 'आचार्य',             en: 'Acharya',            sub_hi: 'in Phalit Jyotish',      sub_en: 'in Phalit Jyotish' },
  { icon: Sparkles,      hi: 'गुरुकुल शिक्षा',     en: 'Gurukul Education',  sub_hi: 'शास्त्रीय परंपरा',       sub_en: 'Classical Tradition' },
  { icon: Globe2,        hi: 'Dubai Clients',       en: 'Dubai Clients',      sub_hi: 'Since 2001',             sub_en: 'Since 2001' },
  { icon: Users,         hi: '33,300+',             en: '33,300+',            sub_hi: 'YouTube Subscribers',    sub_en: 'YouTube Subscribers' },
  { icon: Star,          hi: '4.9 ★ · 847 reviews', en: '4.9 ★ · 847 reviews', sub_hi: 'Google Verified',       sub_en: 'Google Verified' },
];

// Media outlets — use plain text logotypes (no external image fetches → fast & safe).
// Each outlet is a small "as-seen-on" badge.
const MEDIA = [
  { name: 'Zee News',   tone: 'from-red-600 to-red-500' },
  { name: 'Aaj Tak',    tone: 'from-amber-600 to-yellow-500' },
  { name: 'ABP News',   tone: 'from-rose-700 to-red-600' },
  { name: 'News18',     tone: 'from-blue-700 to-sky-600' },
  { name: 'News Nation',tone: 'from-emerald-700 to-teal-600' },
];

export default function TrustBlock() {
  const { lang } = useLang();
  const isHi = lang === 'hi';

  return (
    <section
      id="trust-block"
      data-testid="trust-block"
      className="relative py-16 sm:py-20 overflow-hidden"
    >
      {/* Soft gold/purple gradient background — distinct from Hero & next sections */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0f0508] via-[#1a0710] to-[#0f0508]" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      <div className="absolute -top-24 left-1/4 w-96 h-96 bg-gold/5 rounded-full blur-[120px]" />
      <div className="absolute -bottom-24 right-1/4 w-96 h-96 bg-purple/10 rounded-full blur-[120px]" />

      <div className="container-custom relative z-10">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-10"
        >
          <p className={`text-xs uppercase text-gold/80 font-semibold ${isHi ? 'tracking-normal' : 'tracking-[0.35em]'}`}>
            {isHi ? 'क्यों चुनें आचार्य विवेक मुदगल जी' : 'Why Choose Acharya Vivek Mudgal'}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl text-text-main mt-3 tracking-tight">
            {isHi ? (
              <>तीन दशकों का <span className="text-gold-gradient">वैदिक अनुभव</span></>
            ) : (
              <>Three decades of <span className="text-gold-gradient">Vedic mastery</span></>
            )}
          </h2>
          <div className="divine-divider mt-5 max-w-xs mx-auto" />
        </motion.div>

        {/* Stats Grid — 6 trust pillars */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {STATS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                className="relative rounded-2xl border border-gold/20 bg-white/[0.025] backdrop-blur-md p-4 sm:p-5 text-center hover:border-gold/50 hover:bg-gold/5 transition-all"
                data-testid={`trust-stat-${i}`}
              >
                <div className="w-10 h-10 sm:w-11 sm:h-11 mx-auto rounded-xl bg-gradient-to-br from-gold/20 to-purple/15 border border-gold/30 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-gold" />
                </div>
                <p className="font-heading text-base sm:text-lg text-gold-gradient leading-tight">
                  {isHi ? s.hi : s.en}
                </p>
                <p className="text-[11px] sm:text-xs text-text-muted mt-1 leading-snug">
                  {isHi ? s.sub_hi : s.sub_en}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Media As-Seen-On Strip */}
        <motion.div
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
          viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-12"
          data-testid="media-strip"
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px flex-1 max-w-[80px] bg-gold/25" />
            <p className={`inline-flex items-center gap-2 text-[10px] sm:text-xs uppercase text-gold/80 font-semibold ${isHi ? 'tracking-normal' : 'tracking-[0.3em]'}`}>
              <Tv2 className="w-3.5 h-3.5" />
              {isHi ? 'मीडिया में चर्चित' : 'As Featured On'}
            </p>
            <span className="h-px flex-1 max-w-[80px] bg-gold/25" />
          </div>

          <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3">
            {MEDIA.map((m) => (
              <span
                key={m.name}
                className={`inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r ${m.tone} text-white text-[11px] sm:text-sm font-bold tracking-wide shadow-md ring-1 ring-white/10`}
                data-testid={`media-${m.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {m.name}
              </span>
            ))}
          </div>
        </motion.div>

        {/* CTAs — convert trust to action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <a
            href="#book-consultation"
            onClick={() => trackConsultationCTAClick('trust_block')}
            className="btn-gold inline-flex items-center gap-2 text-base"
            data-testid="trust-book-cta"
          >
            <Sparkles className="w-4 h-4" />
            {isHi ? 'अभी परामर्श बुक करें' : 'Book Consultation Now'}
          </a>
          <a
            href="https://wa.me/919311369729?text=Namaste%20Acharya%20ji,%20I%20would%20like%20to%20consult."
            target="_blank" rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick('trust_block')}
            className="inline-flex items-center gap-2 px-6 py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-full font-semibold transition-all text-base"
            data-testid="trust-whatsapp-cta"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            {isHi ? 'WhatsApp पर बात करें' : 'Chat on WhatsApp'}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
